// AboutPage.js - Adam International Academy Documents Version
import './AboutPage.css';
import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';

// Import images
import heroImg from '../../assets/about/about-hero.jpg';
import historyImg from '../../assets/about/about-history.jpg';
import visionImg from '../../assets/about/about-vision.png';
import missionImg from '../../assets/about/about-mission.png';
import valuesImg from '../../assets/about/about-values.jpg';
import communityImg from '../../assets/about/about-community.jpg';
import facultyImg from '../../assets/about/about-faculty.jpg';
import successImg from '../../assets/about/about-success.jpg';
import partnersImg from '../../assets/about/about-partners.jpg';

function AboutPage() {
  const [activeTimeline, setActiveTimeline] = useState(0);
  const [visibleSections, setVisibleSections] = useState({});
  const sectionRefs = useRef({});

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => ({
              ...prev,
              [entry.target.dataset.section]: true
            }));
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px' }
    );

    // Observe all sections with data-section attribute
    Object.values(sectionRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => {
      Object.values(sectionRefs.current).forEach(ref => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  // Timeline data from documents
  const timelineData = [
    { year: "2018", title: "Foundation in Bahrain", description: "Adam Center for Consulting, Research and Studies established in Bahrain, focusing on engineering, economic and administrative consulting." },
    { year: "2020", title: "Adam International Academy Founded", description: "AIA e-Learning Academy established by Dr. M. Haydar with 19 students, focusing on capacity development and SDG alignment." },
    { year: "2021", title: "University Partnerships", description: "Partnerships established with Sabahattin Zaim University, Medipol University, and American International Theism University." },
    { year: "2022", title: "Global Expansion", description: "Operations expanded to Africa, Europe and America. Student count exceeded 500 with 40+ faculty members from 30 countries." },
    { year: "2023", title: "SDG Integration", description: "All programs fully aligned with Sustainable Development Goals (SDGs), especially SDG 4 on quality education." },
    { year: "2024", title: "Multilingual Programs", description: "Launched multilingual eLearning courses in English, Arabic and Turkish with 45+ programs." }
  ];

  // Faculty specialties from documents
  const facultySpecialties = [
    "Sustainable Development (SDGs)", "Multilingual Education", "Innovation", 
    "Capacity Development", "Policy Analysis", "International Relations",
    "Digital Transformation", "Artificial Intelligence (AI)", "Data Science", 
    "Cybersecurity", "Project Management", "Corporate Governance",
    "Renewable Energy", "Engineering Consultancy", "Legal Studies",
    "Economic Feasibility", "Environmental Studies"
  ];

  // Partner categories from AIA Partners document
  const partnerCategories = [
    { name: "United Nations Agencies", count: 8 },
    { name: "Development Agencies", count: 12 },
    { name: "International Organizations", count: 15 },
    { name: "Private Sector", count: 25 },
    { name: "Academic Institutions", count: 30 },
    { name: "Research Centers", count: 20 },
    { name: "Civil Society Organizations", count: 18 },
    { name: "NGOs", count: 22 }
  ];

  // Core values from documents
  const coreValues = [
    { icon: "🎯", title: "Excellence", description: "Commitment to highest academic standards and quality education" },
    { icon: "🌐", title: "Multilingualism", description: "Education accessible in multiple languages for global reach" },
    { icon: "💡", title: "Innovation", description: "Continuous integration of new ideas, methodologies and technologies" },
    { icon: "🤝", title: "Partnership", description: "Collaborative approach with strategic partners worldwide" },
    { icon: "🌍", title: "Sustainability", description: "Aligned with SDG 2030 and sustainable development principles" },
    { icon: "👥", title: "Inclusivity", description: "Gender and culturally sensitive programs for all" }
  ];

  return (
    <div className="ap-wrapper">

      {/* Hero - Updated with official name */}
      <section
        className="ap-hero"
        style={{ backgroundImage: `url(${heroImg})` }}
      >
        <div className="ap-hero-overlay">
          <div className="ap-hero-content">
            <h1>About Adam International Academy</h1>
            <p>Academy of Science, Technology and Development</p>
            <p className="ap-hero-subtitle">Without Borders, Achieve With Excellence</p>
          </div>
        </div>
      </section>

      {/* Foundation and History - Updated from documents */}
      <section 
        className="ap-section"
        ref={el => sectionRefs.current.history = el}
        data-section="history"
      >
        <div className={`ap-container ap-section-content ${visibleSections.history ? 'visible' : ''}`}>
          <div className="ap-section-text">
            <h2 className="ap-section-title">🏛 Foundation & History</h2>
            <p>
              <strong>Adam International Academy of Science, Technology and Development</strong> is a global educational 
              and training institution specialized in applied sciences, humanities and social studies. The academy was 
              founded in 2020, building upon the expertise of Adam Center for Consulting, Research and Studies established 
              in Bahrain in 2018.
            </p>
            <p>
              Based on the principles of educational excellence, leadership development, and service to society, the 
              academy has aimed to create impact on both regional and global scales. The journey that started with 19 
              students continues today with over 2,500 students and more than 100 academic staff from 30 countries.
            </p>
            <p>
              <strong>Our Mission:</strong> To strengthen the capacity of members to achieve the 2030 Agenda for 
              Sustainable Development through transfer of competencies, development of expertise and promotion of innovation.
            </p>
            <div className="ap-stats-brief">
              <div className="ap-stat-brief-item">
                <span className="ap-stat-brief-number">2018</span>
                <span className="ap-stat-brief-label">Founded in Bahrain</span>
              </div>
              <div className="ap-stat-brief-item">
                <span className="ap-stat-brief-number">2020</span>
                <span className="ap-stat-brief-label">AIA e-Learning Established</span>
              </div>
              <div className="ap-stat-brief-item">
                <span className="ap-stat-brief-number">30+</span>
                <span className="ap-stat-brief-label">Countries</span>
              </div>
            </div>
          </div>
          <img src={historyImg} alt="Foundation and History" className="ap-section-image" loading="lazy" />
        </div>
      </section>

      {/* Timeline - Updated with accurate dates */}
      <section 
        className="ap-timeline"
        ref={el => sectionRefs.current.timeline = el}
        data-section="timeline"
      >
        <div className={`ap-container ${visibleSections.timeline ? 'visible' : ''}`}>
          <h2 className="ap-section-title">⏳ Our Journey</h2>
          <div className="ap-timeline-container">
            {timelineData.map((item, index) => (
              <div 
                key={index} 
                className={`ap-timeline-item ${index === activeTimeline ? 'active' : ''}`}
                onClick={() => setActiveTimeline(index)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => e.key === 'Enter' && setActiveTimeline(index)}
              >
                <div className="ap-timeline-year">{item.year}</div>
                <div className="ap-timeline-content">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision & Mission - Updated from documents */}
      <section 
        className="ap-vision-mission"
        ref={el => sectionRefs.current.vision = el}
        data-section="vision"
      >
        <div className={`ap-container ${visibleSections.vision ? 'visible' : ''}`}>
          <h2 className="ap-section-title">💡 Vision & Mission</h2>
          <div className="ap-vm-grid">
            <div className="ap-vm-card">
              <div className="ap-vm-image-container">
                <img src={visionImg} alt="Our Vision" className="ap-vm-image" loading="lazy" />
                <div className="ap-vm-icon">🌍</div>
              </div>
              <div className="ap-vm-content">
                <h3>Our Vision</h3>
                <p>To be a leading global institution in capacity development, innovation and multilingual education, 
                contributing to sustainable economic and social advancement of members worldwide.</p>
              </div>
            </div>
            
            <div className="ap-vm-card">
              <div className="ap-vm-image-container">
                <img src={missionImg} alt="Our Mission" className="ap-vm-image" loading="lazy" />
                <div className="ap-vm-icon">🎯</div>
              </div>
              <div className="ap-vm-content">
                <h3>Our Mission</h3>
                <ul>
                  <li>Strengthen human resources and institutions through transfer of competencies</li>
                  <li>Develop expertise and promote innovation in key sectors</li>
                  <li>Provide accessible, multilingual education aligned with SDG 2030</li>
                  <li>Foster strategic partnerships with UN agencies, universities and NGOs</li>
                  <li>Create an enabling environment for sustainable economic development</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values - Updated from documents */}
      <section 
        className="ap-values"
        ref={el => sectionRefs.current.values = el}
        data-section="values"
      >
        <div className={`ap-container ${visibleSections.values ? 'visible' : ''}`}>
          <h2 className="ap-section-title">💎 Our Core Values</h2>
          <div className="ap-values-grid">
            {coreValues.map((value, index) => (
              <div key={index} className="ap-value-card">
                <div className="ap-value-icon">{value.icon}</div>
                <h4>{value.title}</h4>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Academic Community - Updated with global perspective */}
      <section 
        className="ap-section"
        ref={el => sectionRefs.current.community = el}
        data-section="community"
      >
        <div className={`ap-container ap-section-content reverse ${visibleSections.community ? 'visible' : ''}`}>
          <div className="ap-section-text">
            <h2 className="ap-section-title">👥 Global Academic Community</h2>
            <p>
              Adam International Academy brings together students and academics from over 30 countries, creating a 
              truly international learning environment. Our academic community includes experts from diverse 
              disciplines including sustainable development, international relations, engineering, and technology.
            </p>
            <p>
              With small class structures and personalized faculty interaction, we ensure that each student receives 
              individual attention and mentorship. Our students have 24/7 access to online classrooms and resources, 
              enabling flexible learning across time zones.
            </p>
            <p>
              <strong>Student Body:</strong> 2,500+ students from 30+ countries | <strong>Faculty:</strong> 100+ 
              academics and experts | <strong>Languages:</strong> English, Arabic, Turkish
            </p>
          </div>
          <img src={communityImg} alt="Academic Community" className="ap-section-image" loading="lazy" />
        </div>
      </section>

      {/* Academic Faculty - Updated with specialties from documents */}
      <section 
        className="ap-faculty"
        ref={el => sectionRefs.current.faculty = el}
        data-section="faculty"
      >
        <div className={`ap-container ${visibleSections.faculty ? 'visible' : ''}`}>
          <h2 className="ap-section-title">📚 Academic Faculty & Expertise</h2>
          <div className="ap-faculty-content">
            <img src={facultyImg} alt="Academic Faculty" className="ap-faculty-image" loading="lazy" />
            <div className="ap-faculty-text">
              <p>
                Our faculty members are high-level professionals from approximately 30 countries, committed, dedicated 
                and extremely reliable. They bring expertise in sustainable development, policy analysis, international 
                relations, digital transformation, and capacity development.
              </p>
              <p>
                Each instructor guides students to achieve their academic and professional goals, ensuring that new 
                competencies are embedded in local institutions and tailored to country-specific needs.
              </p>
              <div className="ap-specialties">
                <h4>Areas of Expertise:</h4>
                <div className="ap-specialties-grid">
                  {facultySpecialties.map((specialty, index) => (
                    <span key={index}>{specialty}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Achievements - Updated with realistic metrics */}
      <section 
        className="ap-success"
        ref={el => sectionRefs.current.success = el}
        data-section="success"
      >
        <div className={`ap-container ${visibleSections.success ? 'visible' : ''}`}>
          <h2 className="ap-section-title">🏆 Our Achievements</h2>
          <div className="ap-success-grid">
            <div className="ap-success-card">
              <div className="ap-success-number">2,500+</div>
              <div className="ap-success-text">Students Worldwide</div>
            </div>
            <div className="ap-success-card">
              <div className="ap-success-number">30+</div>
              <div className="ap-success-text">Countries</div>
            </div>
            <div className="ap-success-card">
              <div className="ap-success-number">100+</div>
              <div className="ap-success-text">Faculty Members</div>
            </div>
            <div className="ap-success-card">
              <div className="ap-success-number">45+</div>
              <div className="ap-success-text">Programs Offered</div>
            </div>
            <div className="ap-success-card">
              <div className="ap-success-number">50+</div>
              <div className="ap-success-text">Strategic Partners</div>
            </div>
            <div className="ap-success-card">
              <div className="ap-success-number">8</div>
              <div className="ap-success-text">Learning Formats</div>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Categories - From AIA Partners document */}
      <section 
        className="ap-partner-categories"
        ref={el => sectionRefs.current.partnerCategories = el}
        data-section="partnerCategories"
      >
        <div className={`ap-container ${visibleSections.partnerCategories ? 'visible' : ''}`}>
          <h2 className="ap-section-title">🤝 Strategic Partner Ecosystem</h2>
          <div className="ap-partner-categories-grid">
            {partnerCategories.map((category, index) => (
              <div key={index} className="ap-partner-category-card">
                <div className="ap-partner-category-count">{category.count}+</div>
                <div className="ap-partner-category-name">{category.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Collaborations - Updated with specific partners */}
      <section 
        className="ap-partners"
        ref={el => sectionRefs.current.partners = el}
        data-section="partners"
      >
        <div className={`ap-container ${visibleSections.partners ? 'visible' : ''}`}>
          <h2 className="ap-section-title">🌐 Global Collaborations</h2>
          <div className="ap-partners-content">
            <img src={partnersImg} alt="Global Collaborations" className="ap-partners-image" loading="lazy" />
            <div className="ap-partners-text">
              <p>
                Our graduates and partners include prestigious institutions such as United Nations agencies, 
                development organizations, and leading universities worldwide.
              </p>
              <p>
                <strong>University Partners:</strong> Sabahattin Zaim University (Turkey), Medipol University (Turkey), 
                American International Theism University, University of 28 May (Algeria), ABC Horizon University (Turkey-UK)
              </p>
              <p>
                Through these collaborations, we offer international exchange programs, joint research projects, and 
                globally recognized certifications. Our capacity development materials are shared with UN agencies, 
                development organizations, and NGOs to support global development efforts.
              </p>
              <Link to="/partners" className="ap-partners-button">View All Partners →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* SDG Commitment Section - New from documents */}
      <section 
        className="ap-sdg-commitment"
        ref={el => sectionRefs.current.sdg = el}
        data-section="sdg"
      >
        <div className={`ap-container ${visibleSections.sdg ? 'visible' : ''}`}>
          <h2 className="ap-section-title">🌍 Commitment to SDG 2030</h2>
          <div className="ap-sdg-content">
            <p>
              All our programs are closely aligned with the Sustainable Development Goals and are peer-reviewed by 
              a wide range of experts to ensure content accuracy, quality and coherence. We directly support all SDGs, 
              with particular emphasis on:
            </p>
            <div className="ap-sdg-highlight">
              <strong>SDG 4:</strong> "Ensure inclusive and equitable quality education and promote lifelong learning 
              opportunities for all."
            </div>
            <div className="ap-sdg-features">
              <div className="ap-sdg-feature">
                <span>🌱</span>
                <p>Gender and culturally sensitive programs</p>
              </div>
              <div className="ap-sdg-feature">
                <span>📚</span>
                <p>Lifelong learning opportunities</p>
              </div>
              <div className="ap-sdg-feature">
                <span>🌐</span>
                <p>Multilingual accessible education</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="ap-cta">
        <div className="ap-container">
          <h2>Join Our Global Community</h2>
          <p>Become part of an international network of leaders and change-makers</p>
          <div className="ap-cta-buttons">
            <Link to="/apply" className="ap-cta-button primary">Apply Now</Link>
            <Link to="/contact" className="ap-cta-button secondary">Contact Admissions</Link>
          </div>
        </div>
      </section>

    </div>
  );
}

export default AboutPage;